import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useComponentTheme } from '../useComponentTheme';
import { withSpacingStyles } from '../withSpacingStyles';
import { useAccessibilityContext, DEFAULT_ZOOM_FACTOR, INCREASED_TEXT_LEVEL } from '@vmos2/providers';
import { scaleCSSUnit } from '@vmos2/utils';

type Props = {
  ariaHidden: boolean,
  /**
   * Content importance. Match with `<h1>`, `<h2>`, `<h3>`. You can define all levels for custom theme mode.
   */
  level: 1 | 2 | 3 | 4 | 5,
  className: ?string,
  children: React.Node,
  id?: string,
  mode?: string,
  tag?: string,
  inValid?: boolean,
  margin: 'none' | 'xsmall' | 'small' | 'normal' | 'large',
  align?: 'center' | 'right' | 'left' | 'spaceBetween',
  /**
   * Color prop will override other colors from component's theme mode or `inValid` prop.
   */
  color?: string,
  display: 'block' | 'flex' | 'none',
  noWrap?: boolean,
  initialFont?: boolean,
  dataTest?: string,
  gap?: string,
  alignItems?: string,
  ignoreZoomScaling?: boolean,
  dataTarget?: string,
  ariaDescribedBy?: string,
  ariaLabel?: string,
  tabIndex?: number,
  autoFocus?: boolean,
  autoFocusTimeout?: number,
  route?: string,
};

const _Title = ({
  ariaHidden = false,
  level = 3,
  className,
  color,
  children,
  id,
  mode,
  tag,
  inValid,
  margin = 'normal',
  align,
  display = 'block',
  noWrap,
  initialFont = false,
  dataTest,
  gap,
  alignItems,
  ignoreZoomScaling,
  dataTarget,
  ariaDescribedBy,
  ariaLabel,
  tabIndex,
  autoFocus = false,
  autoFocusTimeout = 0,
  route,
}: Props) => {
  const componentTheme = useComponentTheme(theme => theme.components.Title, mode);
  const { zoomFactor } = useAccessibilityContext();
  const isZoomed = !!zoomFactor && zoomFactor !== DEFAULT_ZOOM_FACTOR;
  const Tag = tag || `h${level}`;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const shouldAutoFocusOnMount = autoFocus && !ariaHidden;

  useEffect(() => {
    let id = null;

    if (titleRef?.current && shouldAutoFocusOnMount) {
      id = window.setTimeout(() => {
        titleRef.current?.focus();
      }, autoFocusTimeout);
    }

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [autoFocusTimeout, shouldAutoFocusOnMount, route]);

  return (
    <Heading
      {...(titleRef && shouldAutoFocusOnMount ? { tabIndex: -1 } : { tabIndex })}
      ref={titleRef}
      as={Tag}
      className={className}
      id={id}
      level={level}
      componentTheme={componentTheme}
      inValid={inValid}
      marginType={margin}
      align={componentTheme.align || align}
      color={color}
      display={display}
      noWrap={noWrap}
      initialFont={initialFont}
      data-test={dataTest}
      gap={gap}
      alignItems={alignItems}
      ignoreZoomScaling={ignoreZoomScaling}
      isZoomed={isZoomed}
      data-target={dataTarget}
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      {...(ariaHidden && { 'aria-hidden': true })}
    >
      {children}
    </Heading>
  );
};

export const Title = withSpacingStyles(_Title);

const marginStyles = {
  xsmall: 'margin: 0 0 8px',
  small: 'margin: 0 0 16px',
  normal: 'margin: 0 0 24px',
  large: 'margin: 0 0 32px',
  none: 'margin: 0',
};

const alignStyles = {
  center: 'text-align: center',
  left: 'text-align: left',
  right: 'text-align: right',
  spaceBetween: 'display: flex; justify-content: space-between',
  itemsCenter: `display: flex; align-items: center`,
};

const Heading = styled('h3', {
  shouldForwardProp: prop =>
    ![
      'align',
      'alignItems',
      'as',
      'componentTheme',
      'display',
      'ignoreZoomScaling',
      'initialFont',
      'inValid',
      'isZoomed',
      'level',
      'marginType',
      'noWrap',
    ].includes(prop),
})`
  ${({ tabIndex }) => (tabIndex !== undefined ? 'outline: none;' : '')}
  display: ${({ display }) => display};
  font-weight: ${({ componentTheme }) => componentTheme.fontWeight};
  color: ${({ componentTheme, theme, inValid, color }) =>
    color || (inValid ? theme.color.danger : componentTheme.color)};
  font-size: ${({ componentTheme, level, ignoreZoomScaling, isZoomed }) =>
    ignoreZoomScaling && isZoomed
      ? scaleCSSUnit(componentTheme.fontSize[`level${level}`], INCREASED_TEXT_LEVEL)
      : componentTheme.fontSize[`level${level}`]};
  font-family: ${({ theme, componentTheme, initialFont }) =>
    componentTheme.secondaryFontFamily && !initialFont ? theme.typography.secondaryFontFamily : 'inherit'};
  ${({ marginType }) => marginStyles[marginType]};
  ${({ align }) => align && alignStyles[align]};
  text-transform: ${({ componentTheme }) => componentTheme.textTransform};
  ${({ noWrap }) => noWrap && 'white-space: nowrap'};
  ${({ gap }) => gap && `gap: ${gap}`};
`;
